import {
    ApiDailyRewardCheckInReqSchema,
    ApiDailyRewardCheckInResSchema,
    type ApiDailyRewardCheckInRes,
    type ApiDailyRewardGrant,
    type ApiDailyRewardSnapshot,
    type ApiSmStateSnapshot,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { authenticatedApiCall } from "../helpers/api";
import { preloadSounds } from "../helpers/audio";
import { applyDailyRewardCheckIn } from "../helpers/dailyRewardCheckIn";
import {
    dailyRewardCollection,
    dailyRewardSounds,
} from "../helpers/dailyRewardCollection";
import i18n from "../i18n/i18n";
import { useDailyRewardCollectionSound } from "./useDailyRewardCollectionSound";

type DailyRewardCheckInOk = Extract<ApiDailyRewardCheckInRes, { status: "OK" }>;

const ELIGIBILITY_REFRESH_BUFFER_MS = 1_000;

type DailyRewardCheckInCache = {
    timezone: string | undefined;
    result: DailyRewardCheckInOk | null;
    refreshAtClientMs: number | null;
    promise: Promise<DailyRewardCheckInOk>;
};

// The cache survives component remounts, but expires at the authoritative
// eligibility instant or immediately when the browser timezone changes.
let checkInCache: DailyRewardCheckInCache | null = null;

export class DailyRewardApiError extends Error {
    readonly response?: Extract<ApiDailyRewardCheckInRes, { status: "ERROR" }>;

    constructor(
        message: string,
        response?: Extract<ApiDailyRewardCheckInRes, { status: "ERROR" }>,
    ) {
        super(message);
        this.name = "DailyRewardApiError";
        this.response = response;
    }
}

function detectedTimezone(): string | undefined {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const parsed = ApiDailyRewardCheckInReqSchema.safeParse({
            ...(timezone ? { timezone } : {}),
        });
        return parsed.success ? parsed.data.timezone : undefined;
    } catch {
        return undefined;
    }
}

function eligibilityTime(snapshot: ApiDailyRewardSnapshot): number {
    return new Date(snapshot.nextEligibleAt).getTime();
}

function matchingCacheRefreshTime(
    snapshot: ApiDailyRewardSnapshot,
): number | null {
    const cache = checkInCache;
    const cachedSnapshot = cache?.result?.dailyReward;
    if (
        !cache ||
        !cachedSnapshot ||
        cache.refreshAtClientMs === null ||
        cachedSnapshot.observedAt !== snapshot.observedAt ||
        cachedSnapshot.nextEligibleAt !== snapshot.nextEligibleAt ||
        cachedSnapshot.eligibilityTimezone !== snapshot.eligibilityTimezone
    ) {
        return null;
    }
    return cache.refreshAtClientMs;
}

function refreshTime(snapshot: ApiDailyRewardSnapshot): number {
    return (
        matchingCacheRefreshTime(snapshot) ??
        eligibilityTime(snapshot) + ELIGIBILITY_REFRESH_BUFFER_MS
    );
}

function needsRefresh(snapshot: ApiDailyRewardSnapshot): boolean {
    const timezone = detectedTimezone();
    if (timezone && timezone !== snapshot.eligibilityTimezone) return true;

    const refreshAt = refreshTime(snapshot);
    return Number.isFinite(refreshAt) && Date.now() >= refreshAt;
}

async function fetchDailyReward(
    timezone: string | undefined,
): Promise<DailyRewardCheckInOk> {
    const request = ApiDailyRewardCheckInReqSchema.parse({
        ...(timezone ? { timezone } : {}),
    });
    const { body } = await authenticatedApiCall(
        "/api/v1/daily-reward/check-in",
        ApiDailyRewardCheckInResSchema,
        {
            method: "POST",
            body: JSON.stringify(request),
        },
    );
    if (body.status === "ERROR") {
        throw new DailyRewardApiError(
            i18n.t("dailyReward.errorWithCode", { code: body.code }),
            body,
        );
    }
    return body;
}

function cachedCheckIn(): Promise<DailyRewardCheckInOk> {
    const timezone = detectedTimezone();
    const cache = checkInCache;
    if (cache && cache.timezone === timezone) {
        if (!cache.result) return cache.promise;
        if (
            (!timezone ||
                timezone === cache.result.dailyReward.eligibilityTimezone) &&
            cache.refreshAtClientMs !== null &&
            Date.now() < cache.refreshAtClientMs
        ) {
            return cache.promise;
        }
    }

    const promise = fetchDailyReward(timezone);
    const cacheEntry: DailyRewardCheckInCache = {
        timezone,
        result: null,
        refreshAtClientMs: null,
        promise,
    };
    checkInCache = cacheEntry;
    void promise.then(
        (result) => {
            if (checkInCache !== cacheEntry) return;
            cacheEntry.result = result;
            const observedAt = new Date(
                result.dailyReward.observedAt,
            ).getTime();
            const eligibleAt = eligibilityTime(result.dailyReward);
            cacheEntry.refreshAtClientMs =
                Number.isFinite(observedAt) && Number.isFinite(eligibleAt)
                    ? Date.now() +
                      Math.max(0, eligibleAt - observedAt) +
                      ELIGIBILITY_REFRESH_BUFFER_MS
                    : eligibilityTime(result.dailyReward) +
                      ELIGIBILITY_REFRESH_BUFFER_MS;
        },
        () => {
            if (checkInCache === cacheEntry) checkInCache = null;
        },
    );
    return promise;
}

function newerSnapshot(
    current: ApiSmStateSnapshot | null,
    incoming: ApiSmStateSnapshot,
): ApiSmStateSnapshot {
    if (!current) return incoming;
    const currentObservedAt = new Date(current.observedAt).getTime();
    const incomingObservedAt = new Date(incoming.observedAt).getTime();
    return currentObservedAt > incomingObservedAt ? current : incoming;
}

export function useDailyReward(enabled: boolean) {
    const { t } = useTranslation();
    const { state, setSlice: setDailyRewardSlice } =
        useSlice("dailyRewardSlice");
    const { setSlice: setSlotMachineSlice } = useSlice("slotMachineSlice");
    const requestRevisionRef = useRef(0);
    const [collectionGrants, setCollectionGrants] = useState<
        ApiDailyRewardGrant[]
    >([]);
    const collectionPresentation = dailyRewardCollection(collectionGrants, t);
    const fadeCollectionSound = useDailyRewardCollectionSound(collectionGrants);

    useEffect(
        () => () => {
            requestRevisionRef.current += 1;
        },
        [],
    );

    const checkIn = useCallback(
        async ({
            openDuringLoad,
            openWhenRewardedNow,
        }: {
            openDuringLoad: boolean;
            openWhenRewardedNow: boolean;
        }) => {
            const requestRevision = ++requestRevisionRef.current;
            setDailyRewardSlice((current) => ({
                loadStatus: "loading",
                loadError: null,
                isDialogOpen: openDuringLoad || current.isDialogOpen,
            }));

            try {
                const body = await cachedCheckIn();
                if (requestRevision !== requestRevisionRef.current) return body;
                if (!body.rewardedNow) {
                    setSlotMachineSlice((current) => ({
                        snapshot: newerSnapshot(
                            current.snapshot,
                            body.slotSnapshot,
                        ),
                        loadStatus: "ready",
                        loadError: null,
                    }));
                }
                setDailyRewardSlice((current) =>
                    applyDailyRewardCheckIn(current, body, openWhenRewardedNow),
                );
                return body;
            } catch (error) {
                if (requestRevision === requestRevisionRef.current) {
                    setDailyRewardSlice({
                        loadStatus: "error",
                        loadError:
                            error instanceof DailyRewardApiError
                                ? error.message
                                : i18n.t("dailyReward.error"),
                    });
                }
                throw error;
            }
        },
        [setDailyRewardSlice, setSlotMachineSlice],
    );

    useEffect(() => {
        if (!enabled || state.loadStatus !== "idle") return;

        void checkIn({
            openDuringLoad: false,
            openWhenRewardedNow: true,
        }).catch((error: unknown) => {
            console.error("[useDailyReward] Automatic check-in failed", error);
        });
    }, [checkIn, enabled, state.loadStatus]);

    useEffect(() => {
        if (!enabled || !state.snapshot) return;
        const snapshot = state.snapshot;

        const refreshAt = refreshTime(snapshot);
        if (!Number.isFinite(refreshAt)) return;
        const delay = Math.max(0, refreshAt - Date.now());
        const timerId = window.setTimeout(() => {
            void checkIn({
                openDuringLoad: false,
                openWhenRewardedNow: true,
            }).catch((error: unknown) => {
                console.error(
                    "[useDailyReward] Eligibility check-in failed",
                    error,
                );
            });
        }, delay);

        return () => window.clearTimeout(timerId);
    }, [checkIn, enabled, state.snapshot]);

    useEffect(() => {
        if (!enabled || !state.snapshot) return;
        const snapshot = state.snapshot;

        const refreshWhenVisible = () => {
            if (
                document.visibilityState !== "visible" ||
                !needsRefresh(snapshot)
            ) {
                return;
            }
            void checkIn({
                openDuringLoad: false,
                openWhenRewardedNow: true,
            }).catch((error: unknown) => {
                console.error(
                    "[useDailyReward] Foreground check-in failed",
                    error,
                );
            });
        };

        document.addEventListener("visibilitychange", refreshWhenVisible);
        return () =>
            document.removeEventListener(
                "visibilitychange",
                refreshWhenVisible,
            );
    }, [checkIn, enabled, state.snapshot]);

    const open = useCallback(() => {
        setDailyRewardSlice({ isDialogOpen: true });
        if (state.snapshot && !needsRefresh(state.snapshot)) return;

        void checkIn({
            openDuringLoad: true,
            openWhenRewardedNow: false,
        }).catch((error: unknown) => {
            console.error("[useDailyReward] Manual check-in failed", error);
        });
    }, [checkIn, setDailyRewardSlice, state.snapshot]);

    const retry = useCallback(() => {
        void checkIn({
            openDuringLoad: true,
            openWhenRewardedNow: false,
        }).catch((error: unknown) => {
            console.error("[useDailyReward] Check-in retry failed", error);
        });
    }, [checkIn]);

    const acknowledge = useCallback(() => {
        const pendingSnapshot = state.pendingSlotSnapshot;
        if (!pendingSnapshot || state.claimState !== "claimable") return null;

        setSlotMachineSlice((current) => ({
            snapshot: newerSnapshot(current.snapshot, pendingSnapshot),
            loadStatus: "ready",
            loadError: null,
        }));
        setDailyRewardSlice({
            claimState: "claimed",
            pendingSlotSnapshot: null,
            isDialogOpen: false,
        });
        return state.grants;
    }, [
        setDailyRewardSlice,
        setSlotMachineSlice,
        state.claimState,
        state.grants,
        state.pendingSlotSnapshot,
    ]);

    const claim = useCallback(() => {
        const grants = acknowledge();
        if (grants) {
            preloadSounds(dailyRewardSounds(grants));
            setCollectionGrants(grants);
        }
    }, [acknowledge]);

    const close = useCallback(() => {
        // Dismissal still applies the earned state, without a celebration.
        acknowledge();
        setDailyRewardSlice({ isDialogOpen: false });
    }, [acknowledge, setDailyRewardSlice]);

    const completeCollection = useCallback((presentationId: string) => {
        setCollectionGrants((current) =>
            current[0]?.grantId === presentationId ? [] : current,
        );
    }, []);

    return {
        ...state,
        collectionPresentation,
        isPresenting: state.isDialogOpen || collectionPresentation !== null,
        completeCollection,
        fadeCollectionSound,
        open,
        close,
        claim,
        retry,
    };
}
